/**
 * Domain errors for the pure business engine.
 *
 * The engine package has zero runtime dependencies and zero IO, so it
 * defines its own error hierarchy locally. Consumers (API, workers, tests)
 * can catch by class to produce user-facing messages.
 */

export class EngineError extends Error {
  public override readonly name: string = 'EngineError'

  public constructor(message: string) {
    super(message)
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

/**
 * Thrown when a time string does not match the expected HH:MM or HH:MM:SS
 * format, or when hour/minute/second values are out of range.
 */
export class InvalidTimeFormatError extends EngineError {
  public override readonly name = 'InvalidTimeFormatError'

  public constructor(input: string) {
    super(`Invalid time format: "${input}". Expected "HH:MM" or "HH:MM:SS".`)
  }
}

/**
 * Thrown when a night-period configuration supplied by the application
 * layer is malformed — i.e. hours out of 0..23, or a range that does not
 * cross midnight (startHour must be strictly greater than endHour).
 *
 * Why it throws rather than returning 0: a night configuration comes from
 * code or tenant administration, never from end-user input. An invalid
 * config is a programming / configuration bug and must fail loudly so it
 * is caught in tests or startup validation, not silently produce 0 night
 * minutes on real driver time sheets.
 */
export class InvalidNightConfigError extends EngineError {
  public override readonly name = 'InvalidNightConfigError'

  public constructor(reason: string) {
    super(`Invalid night configuration: ${reason}.`)
  }
}
